import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { apiService } from '../../services/apiService';

export interface BiomarkerEntry {
  id: string;
  userId: string;
  biomarkerId: string;
  value: number;
  unit: string;
  timestamp: string;
  source: 'manual' | 'lab' | 'wearable';
  notes?: string;
}

export interface VitalEntry {
  id: string;
  userId: string;
  type: string;
  value: number;
  unit: string;
  timestamp: string;
}

interface HealthState {
  biomarkers: BiomarkerEntry[];
  vitals: VitalEntry[];
  isLoading: boolean;
  error: string | null;
  lastUpdated: string | null;
}

const initialState: HealthState = {
  biomarkers: [],
  vitals: [],
  isLoading: false,
  error: null,
  lastUpdated: null,
};

export const fetchBiomarkers = createAsyncThunk(
  'health/fetchBiomarkers',
  async (userId: string) => {
    const res = await apiService.authedGet(`/health/biomarkers/${userId}`);
    return res.json();
  }
);

export const fetchVitals = createAsyncThunk(
  'health/fetchVitals',
  async (userId: string) => {
    const res = await apiService.authedGet(`/health/vitals/${userId}`);
    return res.json();
  }
);

export const addBiomarkerEntry = createAsyncThunk(
  'health/addBiomarker',
  async (entry: Omit<BiomarkerEntry, 'id'>) => {
    const res = await apiService.authedPost('/health/biomarkers', entry);
    return res.json();
  }
);

const healthSlice = createSlice({
  name: 'health',
  initialState,
  reducers: {
    setBiomarkers: (state, action: PayloadAction<BiomarkerEntry[]>) => {
      state.biomarkers = action.payload;
      state.lastUpdated = new Date().toISOString();
    },
    setVitals: (state, action: PayloadAction<VitalEntry[]>) => {
      state.vitals = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearHealthData: (state) => {
      state.biomarkers = [];
      state.vitals = [];
      state.lastUpdated = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBiomarkers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBiomarkers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.biomarkers = action.payload;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchBiomarkers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch biomarkers';
      })
      .addCase(fetchVitals.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchVitals.fulfilled, (state, action) => {
        state.isLoading = false;
        state.vitals = action.payload;
      })
      .addCase(fetchVitals.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch vitals';
      })
      .addCase(addBiomarkerEntry.fulfilled, (state, action) => {
        state.biomarkers.push(action.payload);
      });
  },
});

export const { setBiomarkers, setVitals, clearError, clearHealthData } = healthSlice.actions;
export default healthSlice.reducer;
